import React from 'react';
import styled, {
  css,
  FlattenSimpleInterpolation,
  FlattenInterpolation,
  ThemeProps,
  DefaultTheme,
  keyframes,
} from 'styled-components';
import ReactPerfectScrollbar, { ScrollBarProps } from 'react-perfect-scrollbar';
import ReactResizeDetector from 'react-resize-detector';
import 'react-perfect-scrollbar/dist/css/styles.css';

export { default as ReactPerfectScrollbar } from 'react-perfect-scrollbar';

const defaultStyleOverrides = {
  scrollbarContainer: css``,
  railX: css``,
  thumbX: css``,
  railY: css``,
  thumbY: css``,
};

type StyleOverrides = {
  [key in keyof typeof defaultStyleOverrides]?: (
    FlattenSimpleInterpolation | FlattenInterpolation<ThemeProps<DefaultTheme>>
  )
};

export interface Props {
  color?: string,
  backgroundColor?: string,
  styleOverrides?: StyleOverrides,
  outerComponent?: React.ComponentType<{ className?: string }>,
  perfectScrollbarsOptions?: ScrollBarProps['options'],
  className?: string,
  refOverride?: React.RefObject<ReactPerfectScrollbar>,
}


const PerfectScrollbar: React.FC<Props> = ({
  children,
  backgroundColor = 'rgba(0,0,0,0.01)',
  color = 'lightgrey',
  styleOverrides,
  outerComponent = 'div',
  perfectScrollbarsOptions: perfectScrollbarsOptionsProp,
  className,
  refOverride,
}) => {
  const PSRefDefault = React.useRef<ReactPerfectScrollbar>(null);
  const PSRef = refOverride || PSRefDefault;
  const handleResize = () => PSRef?.current?.updateScroll();

  const defaultPerfectScrollbarsOptions: ScrollBarProps['options'] = {};
  const perfectScrollbarsOptions = {
    ...defaultPerfectScrollbarsOptions,
    ...perfectScrollbarsOptionsProp,
  };

  const styleOverridesWithDefaults = {
    ...defaultStyleOverrides,
    ...styleOverrides,
  };

  return (
    <ScrollbarsOuter
      $backgroundColor={backgroundColor}
      $color={color}
      styleOverrides={styleOverridesWithDefaults}
      as={outerComponent}
      className={className}
    >
      <ScrollbarContainer
        ref={PSRef}
        $styleOverrides={styleOverridesWithDefaults}
        options={perfectScrollbarsOptions}
      >
        {children}
        <ReactResizeDetector handleWidth onResize={handleResize} />
      </ScrollbarContainer>
    </ScrollbarsOuter>
  );
};
export default PerfectScrollbar;


interface ScrollbarOuterProps {
  $backgroundColor: React.CSSProperties['backgroundColor'],
  $color: React.CSSProperties['backgroundColor'],
  styleOverrides: StyleOverrides,
}

const ScrollbarsOuter = styled.div<ScrollbarOuterProps>`
  .ps__rail-x,
  .ps__rail-y {
    animation: ${keyframes`
      from { opacity: 0; }
      to { opacity: 1; }
    `} 0.3s 0.3s both;
  }
  .ps__rail-x {
    height: 12px;
    opacity: 1 !important;
    background-color: ${(p) => p.$backgroundColor};
    ${(p) => p.styleOverrides.railX};
    .ps__thumb-x {
      background-color: ${(p) => p.$color};
      transform: scaleX(0.9);
      height: 8px !important;
      transition: width 0.5s;
      &:hover {
        height: 8px !important;
      }
      ${(p) => p.styleOverrides.thumbX};
    }
  }

  .ps__rail-y {
    width: 12px;
    opacity: 1 !important;
    background-color: ${(p) => p.$backgroundColor};
    ${(p) => p.styleOverrides.railY};
    .ps__thumb-y {
      background-color: ${(p) => p.$color};
      transform: scaleY(0.9);
      width: 8px !important;
      transition: height 0.5s;
      &:hover {
        width: 8px !important;
      }
      ${(p) => p.styleOverrides.thumbY};
    }
  }
`;

interface ScrollbarContainerProps {
  ref: React.MutableRefObject<ReactPerfectScrollbar | null>,
  $styleOverrides: StyleOverrides,
}

const ScrollbarContainer = styled(ReactPerfectScrollbar) <ScrollbarContainerProps>`
  ${(p) => p.$styleOverrides.scrollbarContainer};
`;
